import { useMemo } from 'react';
import { useQuery } from '@apollo/client';

import { GET_CONFIG } from '../gql/queries/appConfig';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  MAP_DEFAULT_CENTER_KEY,
} from '../constants/map';

export interface TenantMapCenter {
  center: { lat: number; lng: number };
  zoom: number;
}

function parseRaw(value: string): TenantMapCenter | null {
  try {
    const parsed = JSON.parse(value);
    const lat = parsed?.lat;
    const lng = parsed?.lng;
    const zoom = parsed?.zoom;
    if (typeof lat !== 'number' || !Number.isFinite(lat) || lat < -90 || lat > 90) {
      return null;
    }
    if (typeof lng !== 'number' || !Number.isFinite(lng) || lng < -180 || lng > 180) {
      return null;
    }
    const safeZoom =
      typeof zoom === 'number' && Number.isFinite(zoom) && zoom >= 1 && zoom <= 20
        ? zoom
        : DEFAULT_MAP_ZOOM;
    return { center: { lat, lng }, zoom: safeZoom };
  } catch {
    return null;
  }
}

/**
 * Parsea el valor crudo de la config `map_default_center`
 * ({"lat":41.79,"lng":1.63,"zoom":8}). Devuelve null si no valida —
 * el caller decide el fallback. Exportada pura para poder testearla.
 */
export function parseMapCenterConfig(
  value: string | null | undefined
): TenantMapCenter | null {
  if (!value) return null;
  const result = parseRaw(value);
  if (result === null) {
    // Config PRESENTE pero inválida = misconfiguración del tenant (JSON roto
    // o rangos imposibles). El fallback cubre, pero esto sí merece señal —
    // a diferencia de ausente/offline, que son estados normales y silenciosos.
    console.warn(
      '[map_default_center] Valor de config inválido, usando fallback:',
      value
    );
  }
  return result;
}

/**
 * Centro/zoom por defecto del mapa para el tenant actual (JWT), con
 * fallback a DEFAULT_MAP_CENTER. NO usa useFeatureFlag (coerciona a
 * boolean); lee GET_CONFIG crudo. Deriva con useMemo directo sobre data
 * (lección BUG #20: nada de useState+useEffect).
 */
export function useTenantMapCenter(): TenantMapCenter {
  const { data } = useQuery(GET_CONFIG, {
    variables: { key: MAP_DEFAULT_CENTER_KEY },
    fetchPolicy: 'cache-first',
    errorPolicy: 'ignore',
  });

  return useMemo(
    () =>
      parseMapCenterConfig(data?.getConfig) ?? {
        center: DEFAULT_MAP_CENTER,
        zoom: DEFAULT_MAP_ZOOM,
      },
    [data?.getConfig]
  );
}
